import React from 'react';
import { styled } from '@mui/material/styles';
import { Typography } from '@mui/material';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

/**
 * NodeSection — Titled section for grouping config fields.
 * Always expanded — no accordion/collapse behavior.
 *
 * Usage:
 *   <NodeSection title="Advanced Options">
 *     <NodeSelect ... />
 *     <NodeMentionEditor ... />
 *   </NodeSection>
 */

const Root = styled('div')(({ theme }) => ({
  width: '100%',
  borderRadius: NODE_UI.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'visible',
}));

const Header = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: `${NODE_UI.spacing.sm} ${NODE_UI.spacing.md}`,
  userSelect: 'none',
  backgroundColor: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.02)'
    : '#fafafa',
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
}));

const Content = styled('div')(({ theme }) => ({
  padding: NODE_UI.spacing.md,
  display: 'flex',
  flexDirection: 'column',
  gap: NODE_UI.spacing.md,
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const NodeSection = React.memo(function NodeSection({
  title,
  children,
  defaultExpanded, // kept for API compat, ignored
  icon,
  sx,
}) {
  return (
    <Root
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
      onPointerDown={stopPropagation}
      sx={sx}
    >
      <Header>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {icon}
          <Title>{title}</Title>
        </div>
      </Header>
      <Content>{children}</Content>
    </Root>
  );
});

export default NodeSection;
